import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddBarrioMutation } from "@/features/auxiliar/barrios/api/add-barrio";
import { DeleteBarrioMutation } from "@/features/auxiliar/barrios/api/delete-ciudad";
import { EditBarrioMutation } from "@/features/auxiliar/barrios/api/edit-barrio";
import { getAllBarrios, useGetAllBarriosQuery } from "@/features/auxiliar/barrios/api/get-all-barrios";
import { GetBarrioQuery } from "@/features/auxiliar/barrios/api/get-barrio";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import AddBarrioForm, { addBarrioFormSchema } from "@/features/auxiliar/barrios/components/add-barrio-form";
import EditBarrioForm, { editBarrioFormSchema } from "@/features/auxiliar/barrios/components/edit-barrio-form";

export default function BarriosPage() {
    const openModal = useOpenModal();
    const hasWritePermiso = useHasPermisos(Permiso.WriteCiudadesAndBarrios);

    const filterData = useFilters({
        filters: [
            { type: "string", label: "Nombre", name: "nombre" },
            { type: "string", label: "Código", name: "codigo" },
            { type: "string", label: "Ciudad", name: "ciudad" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllBarriosQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "nombre", header: "Nombre", render: row => formatString(row.nombre) },
            { key: "codigo", header: "Código", render: row => formatString(row.codigo) },
            { key: "ciudadNombre", header: "Ciudad", render: row => formatString(row.ciudadNombre) },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "barrios"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Barrios">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}> 
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    hasWritePermiso && {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
                            <DataTable.EntryOption.DeleteButton disabled={row.protegido} onClick={() => openModal("delete", row)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Drawer code="add">
            <ModalHeader>Agregar Barrio</ModalHeader>
            <AddBarrioMutation>
                {mutation => <SchemaFormHook
                    schema={addBarrioFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("El barrio se ha agregado correctamente.");
                    }}
                    closeModalOnSubmit
                    submitOnce
                >
                    {form => <AddBarrioForm
                        form={form}
                        margin={3}
                    />}
                </SchemaFormHook>}
            </AddBarrioMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Barrio</ModalHeader>
            <ModalValueExtractor>
                {barrioId => <GetBarrioQuery id={barrioId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {barrio => <EditBarrioMutation id={barrio.id}>
                            {mutation => <SchemaFormHook
                                schema={editBarrioFormSchema}
                                onSubmit={async ({ value }) => {
                                    await mutation.mutateAsync(value);
                                    notify("El barrio se ha editado correctamente.");
                                }}
                                defaultValues={{ ...barrio }}
                                closeModalOnSubmit
                                submitOnce
                            >
                                {form => <EditBarrioForm form={form} padding={3} />}
                            </SchemaFormHook>}
                        </EditBarrioMutation>}
                    </DataFetcher>}
                </GetBarrioQuery>}
            </ModalValueExtractor>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {barrio => <DeleteBarrioMutation id={barrio.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Barrio "${barrio.nombre}"`}
                        description={`¿Estás seguro que quieres borrar el barrio "${barrio.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El barrio "${barrio.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteBarrioMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllBarrios}
            defaultFileTitle="Barrios"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllBarrios}
            defaultFileTitle="Barrios"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllBarrios}
            defaultFileTitle="Barrios"
        />
    </ListPage>
    }
